import {
  ApolloClient, ApolloLink, createHttpLink, InMemoryCache,
  split,
} from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import { getSubscriptionTokenFromCookie } from '@ugi/core';
import { createClient } from 'graphql-ws';
import {
  apolloErrorLink,
  apolloRequestIdLink,
} from './links.ts';

const wsLink = new GraphQLWsLink(createClient({
  url: import.meta.env.VITE_UGI_API_WS_URL,
  connectionParams: () => {
    // Subscription token is stored in a non-httpOnly cookie for JS access
    const subscriptionToken = getSubscriptionTokenFromCookie();

    return {
      authorization: subscriptionToken ? `Bearer ${subscriptionToken}` : undefined,
    };
  },
  keepAlive: 10_000, // ping server every 10 seconds
}));

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_UGI_API_GQL_URL,
  credentials: 'include', // Send cookies with requests
});

const addQueryNameLink = new ApolloLink((operation, forward) => {
  const queryName = operation.operationName;

  operation.setContext(() => ({
    uri: `${import.meta.env.VITE_UGI_API_GQL_URL}?operation=${queryName}`,
  }));

  return forward(operation);
});

// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);

    return (
      definition.kind === 'OperationDefinition'
      && definition.operation === 'subscription'
    );
  },
  wsLink,
  // No need for apolloAuthLink (cookies sent automatically) or apolloRefreshTokenLink (server handles refresh)
  apolloRequestIdLink
    .concat(addQueryNameLink)
    .concat(httpLink),
);

export const apolloClient = new ApolloClient({
  link: apolloErrorLink.concat(splitLink),
  cache: new InMemoryCache({
    possibleTypes: {
      Broadcast: [
        'EmailBroadcast',
        'SmsBroadcast',
      ],
      Document: [
        'CertificateOfStudyDocument',
        'CertificateOfStudyEnglishDocument',
        'CallCertificateDocument',
        'DiplomaShippingRequestDocument',
        'ApplicationForAbsenceDocument',
        'ApplicationForAcademicLeaveDocument',
        'ApplicationForAssessmentRescheduleDocument',
        'ApplicationForCancelAccrualDocument',
        'ApplicationForCourseCreditTransferDocument',
        'ApplicationForCreditRetakeDocument',
        'ApplicationForDeadlineExtensionDocument',
        'ApplicationForExpulsionDocument',
        'ApplicationForIndividualEducationDocument',
        'ApplicationForLastNameChangeDocument',
        'ApplicationForOnlineEducationDocument',
        'ApplicationForOtherGroupAttendanceDocument',
        'ApplicationForReinstatementDocument',
        'ApplicationForSessionRescheduleDocument',
        'ApplicationForTransferDocument',
      ],
    },
    typePolicies: {
      // Журнал змін: `matched` і набір рядків транзакції залежать від фільтра
      // запиту, тож нормалізувати їх за id не можна — інакше повернення до
      // попереднього пошуку на мить показало б рядки іншого.
      AuditTransaction: { keyFields: false },
      AuditLogRow: { keyFields: false },
      Query: {
        fields: {
          groupMembersConnection: relayStylePagination(),
          organizationMembersConnection: relayStylePagination(),
          chatMessages: relayStylePagination(['chatId']),
          studentEvents: relayStylePagination(['studentId', 'filter']),
        },
      },
    },
  }),
});
